/**
 * Mirror client-side de requisitos de graduación (defaults + evaluación).
 * La fuente de verdad editable vive en app_settings vía API pública.
 */

export const GRADUATION_REQUIREMENTS_DEFAULTS = {
  intro:
    'Cumple todos los requisitos y solicita el examen cuando esté listo. Cumplir la lista no gradúa solo.',
  items: [
    {
      key: 'history',
      label: 'Historia personal planteada',
      check: 'history',
      levelMin: 10,
      pendingDetail: 'Escribe tu historia en la pestaña Historia',
      doneDetail: 'Completado',
    },
    {
      key: 'techniques',
      label: 'Cinco técnicas básicas aprendidas',
      check: 'basic_techniques',
      levelMin: 10,
      pendingDetail: 'Pendiente: Bunshin, Henge, Kawarimi, Kakuremino, Nawanuke',
      doneDetail: 'Completado',
    },
    {
      key: 'simulacro',
      label: 'Simulacro de academia completado',
      check: 'staff_flag',
      levelMin: 10,
      pendingDetail: 'Pendiente de desbloqueo',
      doneDetail: 'Completado',
    },
    {
      key: 'posts',
      label: 'Posts en escenas de academia',
      check: 'staff_flag',
      levelMin: 10,
      pendingDetail: 'Pendiente de desbloqueo',
      doneDetail: 'Completado',
    },
    {
      key: 'level',
      label: 'Nivel 10',
      check: 'level',
      levelMin: 10,
      pendingDetail: '',
      doneDetail: 'Alcanzado',
    },
  ],
}

const CHECK_TYPES = new Set(['history', 'level', 'manual', 'basic_techniques', 'staff_flag'])

export const STAFF_FLAG_BY_KEY = Object.freeze({
  simulacro: 'academySimulacroDone',
  posts: 'academyPostsDone',
})

function slugKey(label, index) {
  const base = String(label || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40)
  return base || `req_${index + 1}`
}

export function normalizeGraduationRequirements(raw) {
  let parsed = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      parsed = null
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    return JSON.parse(JSON.stringify(GRADUATION_REQUIREMENTS_DEFAULTS))
  }

  const intro =
    String(parsed.intro ?? '').trim() || GRADUATION_REQUIREMENTS_DEFAULTS.intro

  const sourceItems = Array.isArray(parsed.items) ? parsed.items : []
  const items = []
  const usedKeys = new Set()

  sourceItems.forEach((item, index) => {
    if (!item || typeof item !== 'object') return
    const label = String(item.label || '').trim()
    if (!label) return
    let key = String(item.key || '').trim() || slugKey(label, index)
    key = key.slice(0, 48)
    if (usedKeys.has(key)) key = `${key}_${index + 1}`
    usedKeys.add(key)

    let check = String(item.check || 'manual').trim().toLowerCase()
    if (!CHECK_TYPES.has(check)) check = 'manual'

    let levelMin = Number(item.levelMin)
    if (!Number.isFinite(levelMin) || levelMin < 1) levelMin = 10
    levelMin = Math.floor(levelMin)

    items.push({
      key,
      label,
      check,
      levelMin,
      pendingDetail: String(item.pendingDetail ?? '').trim(),
      doneDetail: String(item.doneDetail ?? '').trim() || 'Completado',
    })
  })

  if (items.length === 0) {
    return JSON.parse(JSON.stringify(GRADUATION_REQUIREMENTS_DEFAULTS))
  }

  return { intro, items }
}

export function evaluateGraduationItems(config, character = {}) {
  const { intro, items } = normalizeGraduationRequirements(config)
  const historyOk = String(character?.history || '').trim().length > 0
  const level = Number(character?.level || 1)
  const requiredIds = (Array.isArray(character?.academyBasicTechniqueIds)
    ? character.academyBasicTechniqueIds
    : []
  )
    .map((n) => Number(n))
    .filter((n) => Number.isInteger(n) && n > 0)
  const learnedSet = new Set(
    (Array.isArray(character?.learnedTechniqueIds) ? character.learnedTechniqueIds : [])
      .map((n) => Number(n))
      .filter((n) => Number.isInteger(n) && n > 0)
  )

  const evaluated = items.map((item) => {
    let done = false
    let detail = item.pendingDetail || 'Pendiente'

    if (item.check === 'history') {
      done = historyOk
      detail = done ? item.doneDetail || 'Completado' : item.pendingDetail || 'Escribe tu historia'
    } else if (item.check === 'level') {
      const min = item.levelMin || 10
      done = level >= min
      detail = done
        ? item.doneDetail || 'Alcanzado'
        : item.pendingDetail || `Nivel actual: ${level} / ${min}`
    } else if (item.check === 'basic_techniques') {
      const total = requiredIds.length || 5
      const have = requiredIds.length
        ? requiredIds.filter((id) => learnedSet.has(id)).length
        : 0
      done = requiredIds.length > 0 && have >= requiredIds.length
      detail = done
        ? item.doneDetail || 'Completado'
        : item.pendingDetail
          ? `${item.pendingDetail} (${have}/${total})`
          : `Aprendidas: ${have} / ${total}`
    } else if (
      item.check === 'staff_flag' ||
      (item.check === 'manual' && STAFF_FLAG_BY_KEY[item.key])
    ) {
      const flagKey = STAFF_FLAG_BY_KEY[item.key] || item.flagField
      const flagVal = flagKey ? character?.[flagKey] : false
      done = flagVal === true || flagVal === 1 || Number(flagVal) === 1
      detail = done
        ? item.doneDetail || 'Completado'
        : item.pendingDetail || 'Pendiente de desbloqueo'
    } else {
      done = false
      detail = item.pendingDetail || 'Pendiente de desbloqueo'
    }

    return {
      key: item.key,
      label: item.label,
      done,
      detail,
    }
  })

  return { intro, items: evaluated }
}

export function pickGraduationFromSettings(data = {}) {
  if (data.graduation_intro != null || Array.isArray(data.graduation_items)) {
    return normalizeGraduationRequirements({
      intro: data.graduation_intro,
      items: data.graduation_items,
    })
  }
  return normalizeGraduationRequirements(data.graduation_requirements)
}
